import { SubmissionStatus } from '@bp/bp-graphql-types';
import { ModalBottomButtons } from '@bp/ui-components';
import { FileListUploader } from 'components/Files/components/FileList/FileListUploader';
import { BpFile } from 'components/Files/hooks/useFiles';
import { TipTapEditor } from 'components/TipTap/TipTapEditor';
import { Form, Formik, FormikHelpers } from 'formik';
import { useActivityProtocol } from 'hooks/matrix/useActivityProtocol';
import { useRequireParams } from 'hooks/useRequireParams';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { fileEntriesConnect } from 'utils/connectLib';
import { NotificationReason } from 'utils/matrixClient';
import { showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';
import { UpdateSubmissionsMutation, useSubmissions } from '../hooks/useSubmissions';

type SubmissionAnswerFormType = {
  status: SubmissionStatus;
  text: string | null;
  files: BpFile[];
};

type SubmissionAnswerFormProps = {
  submissionUuid: string;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const SubmissionAnswerForm = ({ submissionUuid, onLoad, onClose }: SubmissionAnswerFormProps) => {
  const { t } = useTranslation();
  const { courseUuid, assignmentUuid } = useRequireParams(['courseUuid', 'assignmentUuid']);

  const [, updateSubmission] = useMutation(UpdateSubmissionsMutation);

  const { sendMessage } = useActivityProtocol();

  const { submissions, refetch, ConfirmSubmitDialog, confirmSubmit } = useSubmissions({
    courseUuid,
    assignmentUuid,
    submissionUuid,
  });

  const submission = submissions.find((s) => s.uuid === submissionUuid);
  if (!submission) throw new Error('Submission not found');

  const material = submission.material;

  const initialValues: SubmissionAnswerFormType = {
    status: submission.status,
    text: material.text ?? null,
    files: material.fileEntries ?? [],
  };

  async function handleSubmit(values: SubmissionAnswerFormType, helpers: FormikHelpers<SubmissionAnswerFormType>) {
    if (values.status === SubmissionStatus.New) {
      const confirm = await confirmSubmit();
      if (!confirm) return;
    }

    onLoad(true);

    const { error } = await updateSubmission({
      where: {
        uuid: submissionUuid,
      },
      update: {
        status: values.status,
        material: {
          update: {
            node: {
              text: values.text,
              fileEntries: [fileEntriesConnect(values.files)],
            },
          },
        },
      },
    });

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessUpdateToast();
      refetch();
      if (values.status === SubmissionStatus.New) {
        await sendMessage(submissionUuid, NotificationReason.SubmissionNew);
      }
    }

    onLoad(false);
    helpers.resetForm();
    onClose();
  }

  return (
    <Formik<SubmissionAnswerFormType> initialValues={initialValues} onSubmit={handleSubmit}>
      {({ setFieldValue, values, dirty, submitForm }) => (
        <Form>
          <TipTapEditor
            name='text'
            defaultValue={values.text}
            onChange={async (text) => {
              await setFieldValue('text', text);
            }}
            isDense
            className='mb-7'
          />
          <FileListUploader
            files={values.files}
            onFileUpload={async (file) => {
              await setFieldValue('files', [...values.files, file]);
            }}
            onFileRename={async (file) => {
              await setFieldValue(
                'files',
                values.files.map((f) => (f.uuid === file.uuid ? file : f)),
              );
            }}
            onFilesDelete={async (uuids) => {
              await setFieldValue(
                'files',
                values.files.filter((f) => !uuids.includes(f.uuid)),
              );
            }}
          />
          <ModalBottomButtons
            closeButton={{ callback: () => onClose() }}
            additionalButtons={[
              {
                text: t('submissions.saveAsDraft'),
                callback: async () => {
                  await setFieldValue('status', SubmissionStatus.Draft);
                  submitForm();
                },
                disabled:
                  (values.status !== SubmissionStatus.Todo &&
                    values.status !== SubmissionStatus.Draft &&
                    values.status !== SubmissionStatus.Feedback) ||
                  !submission.isEditable ||
                  !dirty,
              },
            ]}
            submitButton={{
              text: t('common.send'),
              callback: async () => {
                await setFieldValue('status', SubmissionStatus.New);
                submitForm();
              },
              disabled: !submission.isEditable || (values.status !== SubmissionStatus.Draft && !dirty),
            }}
          />

          <ConfirmSubmitDialog />
        </Form>
      )}
    </Formik>
  );
};
